import { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from './components/Layout';
import { API_BASE_URL } from '../config/ApiConfig';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import TableContainer from './components/TableContainer';
import DataTable from './components/DataTable';
import ListaCompraModal from './components/modals/ListaCompraModal';
import PdfViewerModal from './components/modals/PdfViewerModal';
import { formatDateView, formatTimeView } from './utils/dateUtils';
import ResponsiveShadow from './components/ResponsiveShadow';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import ConfirmModal from './components/modals/ConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import usePagination10 from './components/hooks/usePagination10';
import PaginationBar from './components/PaginationBar';
import Toolbar from './components/Toolbar';
import LoadingModal from './components/modals/LoadingModal';
import useReferenceData from './components/hooks/useReferenceData';

function ListaCompras() {
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const { programas, acoes } = useReferenceData(token, cnpj);
  const [listas, setListas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ data: '', hora: '', idPrograma: '', idAcao: '' });
  const [itens, setItens] = useState([]);
  const [atualizar, setAtualizar] = useState(false);
  const [selectedLista, setSelectedLista] = useState(null);
  const [generating, setGenerating] = useState(false);
  const totalItems = listas.length;
  const { setPagination } = usePagination10(totalItems);
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();

  useEffect(() => {
    fetchListas();
  }, [atualizar]);

  const fetchListas = async () => {
    if (!cnpj) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL.LISTA_COMPRA}/all`, {
        params: { cnpjEscola: cnpj },
        headers: { Authorization: `Bearer ${token}` },
      });
      setListas(res.data || []);
    } catch (err) {
      console.error('Erro ao buscar listas de compras', err);
      showDefaultModal('error', 'Erro ao buscar listas de compras');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenNew = () => {
    setForm({ data: '', hora: '', idPrograma: '', idAcao: '' });
    setItens([{ descricao: '', tipo: 'CUSTEIO', unidade: 'UN', quantidade: '', valorUnitario: '' }]);
    setShowModal(true);
  };

  const handleEdit = (lista) => {
    if (!lista) return;
    const listaDetalhada = listas.find(l => l.id === lista.id) || lista;
    setForm({
      id: listaDetalhada.id,
      data: listaDetalhada.data || '',
      hora: listaDetalhada.hora || '',
      idPrograma: listaDetalhada.idPrograma != null ? String(listaDetalhada.idPrograma) : '',
      idAcao: listaDetalhada.idAcao != null ? String(listaDetalhada.idAcao) : '',
    });
    const itensForm = (listaDetalhada.itens || []).map(item => ({
      descricao: item.descricao || '',
      tipo: item.tipo || 'CUSTEIO',
      unidade: item.unidade || 'UN',
      quantidade: item.quantidade != null ? String(item.quantidade) : '',
      valorUnitario: item.valorUnitario != null ? String(item.valorUnitario) : '',
    }));
    setItens(itensForm.length > 0
      ? itensForm
      : [{ descricao: '', tipo: 'CUSTEIO', unidade: 'UN', quantidade: '', valorUnitario: '' }]);
    setShowModal(true);
  };

  const handleSave = async (payload) => {
    try {
      await axios.post(`${API_BASE_URL.LISTA_COMPRA}/save`, { ...payload, cnpjEscola: cnpj }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShowModal(false);
      setSelectedLista(null);
      setAtualizar(prev => !prev);
    } catch (err) {
      console.error('Erro ao salvar lista de compras', err);
      showDefaultModal('error', 'Erro ao salvar lista de compras');
    }
  };

  const handleDelete = (lista) => {
    showConfirmModal('delete', () => handleConfirmDelete(lista));
  };

  const handleConfirmDelete = async (lista) => {
    try {
      await axios.delete(`${API_BASE_URL.LISTA_COMPRA}/delete`, {
        params: { id: lista.id },
        headers: { Authorization: `Bearer ${token}` },
      });
      setAtualizar(prev => !prev);
      setSelectedLista(null);
    } catch (err) {
      console.error('Erro ao excluir lista de compras', err);
      showDefaultModal('error', 'Erro ao excluir lista de compras');
    }
  };

  const handleGerarAta = async () => {
    if (!selectedLista) return;
    try {
      setGenerating(true);
      const res = await axios.post(`${API_BASE_URL.LISTA_COMPRA}/gerarAta`, null, {
        params: { id: selectedLista.id },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'arraybuffer',
      });
      const disposition = res.headers['content-disposition'] || res.headers['Content-Disposition'];
      let filename = `ATA_COMPRA_${selectedLista.id}.docx`;
      if (disposition) {
        const match = disposition.match(/filename="?([^\";]+)"?/);
        if (match && match[1]) filename = match[1];
      }
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erro ao gerar ata de compra', err);
      showDefaultModal('error', 'Erro ao gerar ata de compra');
    } finally {
      setGenerating(false);
    }
  };

  const handleGerarListaPdf = async () => {
    if (!selectedLista) return;
    try {
      setGenerating(true);
      const res = await axios.post(`${API_BASE_URL.LISTA_COMPRA}/gerarListaPdf`, null, {
        params: { id: selectedLista.id },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'arraybuffer',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const pdfId = `pdf_${Date.now()}`;
      if (!window.pdfStorage) window.pdfStorage = {};
      window.pdfStorage[pdfId] = blob;
      const title = `LISTA_COMPRAS_${selectedLista.id}`;
      const data = { url, title, pdfId };
      sessionStorage.setItem('pdfViewerData', JSON.stringify(data));
      window.dispatchEvent(new CustomEvent('pdfViewerUpdate', { detail: data }));
    } catch (err) {
      console.error('Erro ao gerar lista de compras em PDF', err);
      showDefaultModal('error', 'Erro ao gerar lista de compras em PDF');
    } finally {
      setGenerating(false);
    }
  };

  const handleGerarAtaPdf = async () => {
    if (!selectedLista) return;
    try {
      setGenerating(true);
      const res = await axios.post(`${API_BASE_URL.LISTA_COMPRA}/gerarAtaPdf`, null, {
        params: { id: selectedLista.id },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'arraybuffer',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const pdfId = `pdf_${Date.now()}`;
      if (!window.pdfStorage) window.pdfStorage = {};
      window.pdfStorage[pdfId] = blob;
      const title = `ATA_COMPRA_${selectedLista.id}`;
      const data = { url, title, pdfId };
      sessionStorage.setItem('pdfViewerData', JSON.stringify(data));
      window.dispatchEvent(new CustomEvent('pdfViewerUpdate', { detail: data }));
    } catch (err) {
      console.error('Erro ao gerar ata de compra em PDF', err);
      showDefaultModal('error', 'Erro ao gerar ata de compra em PDF');
    } finally {
      setGenerating(false);
    }
  };

  const somaPorTipo = (lista, tipo) => {
    if (!lista.itens || lista.itens.length === 0) return '0,00';
    const total = lista.itens
      .filter(item => (item.tipo || 'CUSTEIO') === tipo)
      .reduce((acc, item) => acc + (Number(item.quantidade) || 0) * (Number(item.valorUnitario) || 0), 0);
    return total.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const totalLista = (lista) => {
    if (!lista.itens || lista.itens.length === 0) return '0,00';
    const total = lista.itens.reduce((acc, item) => {
      const qtd = Number(item.quantidade) || 0;
      const unit = Number(item.valorUnitario) || 0;
      return acc + qtd * unit;
    }, 0);
    return total.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const descricaoLista = (lista) => {
    const programa = programas.find(p => String(p.id) === String(lista.idPrograma));
    const acao = acoes.find(a => String(a.idPrograma) === String(lista.idPrograma) && String(a.id) === String(lista.idAcao));
    const complemento = [programa?.descricao, acao?.descricao].filter(Boolean).join(' ');
    return complemento
      ? `Lista de Compras Prioritárias - ${complemento}`
      : 'Lista de Compras Prioritárias';
  };

  return (
    <Layout>
      <LoadingModal loading={loading} />
      <ResponsiveShadow>
        <PageHeader title="Lista de Compras" icon="bi-cart3" />
        <PageContent>
          <Toolbar>
            <button className="btn btn-success btn-sm botao-igual d-inline-flex align-items-center" onClick={handleOpenNew}>
              <i className="bi bi-plus-lg me-1"></i>Nova Lista
            </button>
            <button
              className="btn btn-primary btn-sm botao-igual d-inline-flex align-items-center"
              disabled={!selectedLista}
              onClick={() => selectedLista && handleEdit(selectedLista)}
            >
              <i className="bi bi-pencil me-1"></i>Editar
            </button>
            <button
              className="btn btn-primary btn-sm botao-igual d-inline-flex align-items-center"
              disabled={!selectedLista || generating}
              onClick={handleGerarAta}
            >
              <i className="bi bi-file-earmark-word me-1"></i>Gerar Ata
            </button>
            <button
              className="btn btn-secondary btn-sm botao-igual d-inline-flex align-items-center"
              disabled={!selectedLista || generating}
              onClick={handleGerarAtaPdf}
            >
              <i className="bi bi-file-earmark-pdf me-1"></i>Gerar Ata PDF
            </button>
            <button
              className="btn btn-info btn-sm botao-igual d-inline-flex align-items-center"
              disabled={!selectedLista || generating}
              onClick={handleGerarListaPdf}
            >
              <i className="bi bi-file-earmark-text me-1"></i>Gerar Lista
            </button>
            <button
              className="btn btn-danger btn-sm botao-igual d-inline-flex align-items-center"
              disabled={!selectedLista}
              onClick={() => selectedLista && handleDelete(selectedLista)}
            >
              <i className="bi bi-trash me-1"></i>Excluir
            </button>
          </Toolbar>

          <TableContainer isLoading={false}>
            <DataTable head={
              <tr>
                <th scope="col" style={{ width: '3rem' }}>#</th>
                <th scope="col">Descrição</th>
                <th scope="col">Data</th>
                <th scope="col">Hora</th>
                <th scope="col">Itens</th>
                <th scope="col">Custeio (R$)</th>
                <th scope="col">Capital (R$)</th>
                <th scope="col" className="text-end">Total (R$)</th>
              </tr>
            }>
              <tbody>
                {listas.slice(setPagination.startIndex, setPagination.startIndex + setPagination.rowsPerPage).map((l, idx) => (
                  <tr
                    key={l.id}
                    className={selectedLista && selectedLista.id === l.id ? 'table-primary' : ''}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedLista(selectedLista && selectedLista.id === l.id ? null : l)}
                  >
                    <td>{setPagination.startIndex + idx + 1}</td>
                    <td>{descricaoLista(l)}</td>
                    <td>{formatDateView(l.data)}</td>
                    <td>{formatTimeView(l.hora)}</td>
                    <td>{l.itens ? l.itens.length : 0}</td>
                    <td>{somaPorTipo(l, 'CUSTEIO')}</td>
                    <td>{somaPorTipo(l, 'CAPITAL')}</td>
                    <td className="text-end">{totalLista(l)}</td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </TableContainer>
        </PageContent>

        <PaginationBar setPagination={setPagination} />
      </ResponsiveShadow>

      <ConfirmModal {...setConfirmModal} />
      <DefaultModal {...setDefaultModal} />
      <PdfViewerModal />
      <ListaCompraModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleSave}
        form={form}
        setForm={setForm}
        itens={itens}
        setItens={setItens}
        programas={programas}
        acoes={acoes}
      />
    </Layout>
  );
}

export default ListaCompras;
